import { useMemo, type ReactNode } from "react";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import {
    IsTaskOpenContext,
    TaskRowActionsContext,
    TasksContext,
    type TaskRowActions,
    type TasksContextValue,
} from "./tasks.context";

interface TasksContextProviderProps {
    value: TasksContextValue;
    children: ReactNode;
}

/**
 * Provides `TasksContext` and the narrower contexts list rows read instead
 * (`TaskRowActionsContext`, `IsTaskOpenContext`).
 */
export const TasksContextProvider = ({
    value,
    children,
}: TasksContextProviderProps) => {
    const selectedTaskIdRef = useLatestRef(value.selectedTaskId);
    const {
        taskDetailPanelRef,
        setTaskDetailPanelContent,
        requestTaskDetailSection,
    } = value;
    const rowActions: TaskRowActions = useMemo(
        () => ({
            getSelectedTaskId: () => selectedTaskIdRef.current,
            taskDetailPanelRef,
            setTaskDetailPanelContent,
            requestTaskDetailSection,
        }),
        [
            selectedTaskIdRef,
            taskDetailPanelRef,
            setTaskDetailPanelContent,
            requestTaskDetailSection,
        ],
    );

    return (
        <TasksContext.Provider value={value}>
            <TaskRowActionsContext.Provider value={rowActions}>
                <IsTaskOpenContext.Provider
                    value={value.selectedTaskId !== null}
                >
                    {children}
                </IsTaskOpenContext.Provider>
            </TaskRowActionsContext.Provider>
        </TasksContext.Provider>
    );
};
